import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Rss, Plus, Copy, RefreshCw, Trash2, MoreVertical, Loader2, Tv, MonitorPlay, Cast, ListChecks, Radio, Film, Check, Eye } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const PLATFORMS = {
  roku: { label: "Roku", icon: Tv, color: "text-purple-400", format: "Direct Publisher JSON" },
  firetv: { label: "Amazon Fire TV", icon: MonitorPlay, color: "text-sky-400", format: "MRSS Feed" },
  chromecast: { label: "Chromecast / Google TV", icon: Cast, color: "text-emerald-400", format: "JSON Feed" },
};

export default function Feeds() {
  const { user } = useAuth();
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", platforms: ["roku", "firetv", "chromecast"] });
  const [manageTarget, setManageTarget] = useState(null);
  const [streams, setStreams] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [selStreams, setSelStreams] = useState([]);
  const [selRecordings, setSelRecordings] = useState([]);
  const [savingContent, setSavingContent] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);
  const [previewFeed, setPreviewFeed] = useState(null);
  const [previewPlatform, setPreviewPlatform] = useState("roku");
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = async () => {
    try { setFeeds((await api.get("/feeds")).data); }
    catch (e) { toast.error("Failed to load feeds"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [user?.role]);

  const copy = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copied!`); };

  const togglePlatform = (p) => setForm((f) => ({
    ...f,
    platforms: f.platforms.includes(p) ? f.platforms.filter((x) => x !== p) : [...f.platforms, p],
  }));

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error("Feed name is required"); return; }
    if (form.platforms.length === 0) { toast.error("Select at least one platform"); return; }
    setCreating(true);
    try {
      await api.post("/feeds", form);
      toast.success("Feed created");
      setCreateOpen(false);
      setForm({ name: "", description: "", platforms: ["roku", "firetv", "chromecast"] });
      load();
    } catch (e) { toast.error("Failed to create feed"); }
    finally { setCreating(false); }
  };

  const regenerate = async (feed) => {
    try {
      const res = await api.post(`/feeds/${feed.id}/regenerate-token`);
      setFeeds((f) => f.map((x) => (x.id === feed.id ? res.data : x)));
      toast.success("Feed URLs regenerated — old links now invalid");
    } catch (e) { toast.error("Failed to regenerate"); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/feeds/${deleteTarget.id}`);
      setFeeds((f) => f.filter((x) => x.id !== deleteTarget.id));
      toast.success("Feed deleted");
    } catch (e) { toast.error("Failed to delete"); }
    finally { setDeleteTarget(null); }
  };

  const openManage = async (feed) => {
    setManageTarget(feed);
    setSelStreams(feed.stream_ids || []);
    setSelRecordings(feed.recording_ids || []);
    setLoadingContent(true);
    try {
      const [st, rec] = await Promise.all([api.get("/streams"), api.get("/recordings")]);
      setStreams(st.data);
      setRecordings(rec.data);
    } catch (e) { toast.error("Failed to load content"); }
    finally { setLoadingContent(false); }
  };

  const toggleSel = (list, setList, id) =>
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const saveContent = async () => {
    setSavingContent(true);
    try {
      const res = await api.put(`/feeds/${manageTarget.id}`, { stream_ids: selStreams, recording_ids: selRecordings });
      setFeeds((f) => f.map((x) => (x.id === manageTarget.id ? res.data : x)));
      toast.success("Feed content updated");
      setManageTarget(null);
    } catch (e) { toast.error("Failed to save content"); }
    finally { setSavingContent(false); }
  };

  const fetchPreview = async (feed, platform) => {
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const res = await api.get(`/feeds/${feed.id}/preview/${platform}`);
      setPreviewData(res.data);
    } catch (e) { toast.error("Failed to load preview"); }
    finally { setPreviewLoading(false); }
  };

  const openPreview = (feed, platform) => {
    setPreviewFeed(feed);
    setPreviewPlatform(platform);
    fetchPreview(feed, platform);
  };

  const switchPreviewPlatform = (platform) => {
    setPreviewPlatform(platform);
    fetchPreview(previewFeed, platform);
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Feeds</h1>
          <p className="text-gray-400 mt-1.5 text-sm">Generate connected-TV app feed URLs for Roku, Amazon Fire TV and Chromecast / Google TV.</p>
        </div>
        <button data-testid="create-feed-button" onClick={() => setCreateOpen(true)} className="gold-gradient hover:brightness-110 text-black font-semibold px-4 py-2.5 rounded-xl flex items-center gap-2 shadow-lg shadow-amber-500/20 transition-all active:scale-[0.98] w-max cursor-pointer">
          <Plus className="h-4 w-4" /> Create Feed
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-32"><Loader2 className="h-6 w-6 text-amber-400 animate-spin" /></div>
      ) : feeds.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-gray-800 rounded-2xl">
          <div className="h-14 w-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto"><Rss className="h-7 w-7 text-amber-400" /></div>
          <p className="mt-4 text-white font-medium">No feeds yet</p>
          <p className="text-gray-500 text-sm mt-1">Create a feed to publish your channel to CTV platforms.</p>
        </div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-2">
          {feeds.map((feed, i) => (
            <motion.div key={feed.id} data-testid={`feed-card-${feed.id}`} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.05, 0.4) }}
              className="bg-card border border-gray-800 rounded-2xl p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-11 w-11 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0"><Rss className="h-5 w-5 text-amber-400" /></div>
                  <div className="min-w-0">
                    <p className="font-semibold text-white truncate">{feed.name}</p>
                    <p className="text-xs text-gray-500 truncate">{feed.description || "No description"}</p>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid={`feed-menu-${feed.id}`} className="h-8 w-8 rounded-lg text-gray-500 hover:text-white hover:bg-gray-800 flex items-center justify-center transition-colors"><MoreVertical className="h-4 w-4" /></button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-card border-gray-800 text-gray-200">
                    <DropdownMenuItem data-testid={`feed-manage-${feed.id}`} onClick={() => openManage(feed)} className="cursor-pointer focus:bg-gray-800"><ListChecks className="h-4 w-4 mr-2" /> Manage Content</DropdownMenuItem>
                    <DropdownMenuItem data-testid={`feed-regenerate-${feed.id}`} onClick={() => regenerate(feed)} className="cursor-pointer focus:bg-gray-800"><RefreshCw className="h-4 w-4 mr-2" /> Regenerate URLs</DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-gray-800" />
                    <DropdownMenuItem onClick={() => setDeleteTarget(feed)} className="cursor-pointer text-red-400 focus:bg-red-500/10 focus:text-red-400"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="mt-4 space-y-2.5">
                {feed.platforms.map((p) => {
                  const info = PLATFORMS[p];
                  const Icon = info.icon;
                  const url = feed.urls?.[p] || "";
                  return (
                    <div key={p} data-testid={`feed-url-${feed.id}-${p}`} className="rounded-xl border border-gray-800 bg-gray-950/60 p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className={"h-4 w-4 " + info.color} />
                        <span className="text-sm font-medium text-white">{info.label}</span>
                        <span className="text-[10px] text-gray-500 border border-gray-700 rounded px-1.5 py-0.5 ml-auto">{info.format}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-[11px] text-amber-300 font-mono truncate bg-black/40 rounded-lg px-2.5 py-1.5 border border-gray-800">{url}</code>
                        <button data-testid={`preview-feed-${feed.id}-${p}`} onClick={() => openPreview(feed, p)} className="h-8 w-8 rounded-lg bg-black/40 border border-gray-800 text-gray-400 hover:text-amber-400 hover:border-amber-500/40 flex items-center justify-center transition-colors shrink-0" title="Live preview"><Eye className="h-4 w-4" /></button>
                        <button data-testid={`copy-feed-${feed.id}-${p}`} onClick={() => copy(url, `${info.label} URL`)} className="h-8 w-8 rounded-lg bg-black/40 border border-gray-800 text-gray-400 hover:text-amber-400 hover:border-amber-500/40 flex items-center justify-center transition-colors shrink-0"><Copy className="h-4 w-4" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <button data-testid={`feed-manage-btn-${feed.id}`} onClick={() => openManage(feed)} className="mt-4 w-full rounded-xl border border-gray-800 hover:border-amber-500/40 bg-gray-950/40 px-4 py-2.5 flex items-center justify-between text-sm text-gray-300 hover:text-white transition-colors">
                <span className="flex items-center gap-2"><ListChecks className="h-4 w-4 text-amber-400" /> Manage Content</span>
                <span className="text-xs text-gray-500">{(feed.stream_ids?.length || 0)} streams · {(feed.recording_ids?.length || 0)} recordings</span>
              </button>
            </motion.div>
          ))}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-card border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Create Feed</DialogTitle>
            <DialogDescription className="text-gray-400">Generate CTV app feed URLs for the selected platforms.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-gray-300">Feed Name</Label>
              <Input data-testid="new-feed-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Worship Channel" className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Description</Label>
              <Textarea data-testid="new-feed-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 resize-none" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Platforms</Label>
              <div className="grid gap-2">
                {Object.entries(PLATFORMS).map(([k, info]) => {
                  const Icon = info.icon;
                  const on = form.platforms.includes(k);
                  return (
                    <button key={k} type="button" data-testid={`platform-toggle-${k}`} onClick={() => togglePlatform(k)}
                      className={"flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors " + (on ? "border-amber-500/50 bg-amber-500/10" : "border-gray-800 bg-gray-950 hover:border-gray-700")}>
                      <Icon className={"h-5 w-5 " + info.color} />
                      <div className="flex-1">
                        <p className="text-sm text-white font-medium">{info.label}</p>
                        <p className="text-[11px] text-gray-500">{info.format}</p>
                      </div>
                      <span className={"h-4 w-4 rounded-full border-2 " + (on ? "bg-amber-500 border-amber-500" : "border-gray-600")} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setCreateOpen(false)} className="px-4 py-2 rounded-lg text-gray-400 hover:text-white text-sm">Cancel</button>
            <button data-testid="submit-create-feed" onClick={handleCreate} disabled={creating} className="gold-gradient text-black font-semibold px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-60 cursor-pointer">
              {creating && <Loader2 className="h-4 w-4 animate-spin" />} Create Feed
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-card border-gray-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this feed?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">"{deleteTarget?.name}" and all its CTV URLs will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-feed" onClick={handleDelete} className="bg-red-600 hover:bg-red-500 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!manageTarget} onOpenChange={(o) => !o && setManageTarget(null)}>
        <DialogContent className="bg-card border-gray-800 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Manage Feed Content</DialogTitle>
            <DialogDescription className="text-gray-400">Choose which streams and recordings appear in "{manageTarget?.name}" across all CTV platforms.</DialogDescription>
          </DialogHeader>
          {loadingContent ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 text-amber-400 animate-spin" /></div>
          ) : (
            <div className="space-y-5 py-1">
              <div>
                <p className="text-[10px] font-semibold text-gray-500 tracking-widest mb-2 flex items-center gap-1.5"><Radio className="h-3.5 w-3.5 text-amber-400" /> STREAMS ({selStreams.length})</p>
                {streams.length === 0 ? <p className="text-sm text-gray-500">No streams available.</p> : (
                  <div className="space-y-1.5">
                    {streams.map((st) => {
                      const on = selStreams.includes(st.id);
                      return (
                        <button key={st.id} type="button" data-testid={`feed-stream-${st.id}`} onClick={() => toggleSel(selStreams, setSelStreams, st.id)}
                          className={"w-full flex items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors " + (on ? "border-amber-500/50 bg-amber-500/10" : "border-gray-800 bg-gray-950 hover:border-gray-700")}>
                          <span className={"h-4 w-4 rounded flex items-center justify-center border " + (on ? "bg-amber-500 border-amber-500" : "border-gray-600")}>{on && <Check className="h-3 w-3 text-black" />}</span>
                          <span className="flex-1 min-w-0"><span className="text-sm text-white truncate block">{st.title}</span></span>
                          <span className={"text-[10px] px-1.5 py-0.5 rounded-full " + (st.status === "active" ? "bg-emerald-500/15 text-emerald-400" : "bg-gray-500/15 text-gray-400")}>{st.status === "active" ? "Live" : "Offline"}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div>
                <p className="text-[10px] font-semibold text-gray-500 tracking-widest mb-2 flex items-center gap-1.5"><Film className="h-3.5 w-3.5 text-amber-400" /> RECORDINGS ({selRecordings.length})</p>
                {recordings.length === 0 ? <p className="text-sm text-gray-500">No recordings yet.</p> : (
                  <div className="space-y-1.5">
                    {recordings.map((rec) => {
                      const on = selRecordings.includes(rec.id);
                      return (
                        <button key={rec.id} type="button" data-testid={`feed-recording-${rec.id}`} onClick={() => toggleSel(selRecordings, setSelRecordings, rec.id)}
                          className={"w-full flex items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors " + (on ? "border-amber-500/50 bg-amber-500/10" : "border-gray-800 bg-gray-950 hover:border-gray-700")}>
                          <span className={"h-4 w-4 rounded flex items-center justify-center border " + (on ? "bg-amber-500 border-amber-500" : "border-gray-600")}>{on && <Check className="h-3 w-3 text-black" />}</span>
                          <span className="flex-1 min-w-0 text-sm text-white truncate">{rec.title}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setManageTarget(null)} className="px-4 py-2 rounded-lg text-gray-400 hover:text-white text-sm">Cancel</button>
            <button data-testid="save-feed-content" onClick={saveContent} disabled={savingContent} className="gold-gradient text-black font-semibold px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-60 cursor-pointer">
              {savingContent && <Loader2 className="h-4 w-4 animate-spin" />} Save Content
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewFeed} onOpenChange={(o) => !o && setPreviewFeed(null)}>
        <DialogContent className="bg-card border-gray-800 text-white max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2"><Eye className="h-5 w-5 text-amber-400" /> Feed Live Preview</DialogTitle>
            <DialogDescription className="text-gray-400">Exactly what each device receives from "{previewFeed?.name}".</DialogDescription>
          </DialogHeader>

          <div className="flex gap-2 border-b border-gray-800 pb-3">
            {(previewFeed?.platforms || []).map((p) => {
              const info = PLATFORMS[p];
              const Icon = info.icon;
              const active = previewPlatform === p;
              return (
                <button key={p} data-testid={`preview-tab-${p}`} onClick={() => switchPreviewPlatform(p)}
                  className={"flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors " + (active ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "bg-gray-950 text-gray-400 border-gray-800 hover:text-white")}>
                  <Icon className={"h-4 w-4 " + info.color} /> {info.label}
                </button>
              );
            })}
          </div>

          <div className="flex-1 overflow-auto min-h-[200px] relative">
            {previewLoading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 text-amber-400 animate-spin" /></div>
            ) : previewData ? (
              <>
                <div className="flex items-center justify-between sticky top-0 bg-card py-2">
                  <span className="text-[10px] uppercase tracking-widest text-gray-500">{previewData.format} · {previewPlatform}</span>
                  <button data-testid="copy-preview-content" onClick={() => copy(previewData.content, "Feed content")} className="text-xs flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20"><Copy className="h-3.5 w-3.5" /> Copy</button>
                </div>
                <pre data-testid="preview-content-block" className="bg-gray-950 border border-gray-800 rounded-lg p-4 text-[11px] leading-relaxed font-mono text-gray-300 overflow-x-auto whitespace-pre">{previewData.content}</pre>
              </>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
